/**
 * 旅程条件（出発日・帰着日・大人人数・部屋数）の純粋関数。
 *
 * ★これが「1回だけ入力する条件」の単一の正。
 *   宿の空室検索・レンタカー・航空券・体験予約は**すべてここから**日付と人数を受け取り、
 *   外部予約サイトへは条件をURLに載せた状態で送る（利用者に同じ条件を2度入力させない）。
 *
 * 用語の定義（ここを曖昧にすると各サービスで意味がずれる）:
 *   departIso … **自分の街を出発する日**。＝宿のチェックイン日／レンタカー貸出日／往路の搭乗日。
 *   returnIso … **自分の街に帰り着く日**。＝宿のチェックアウト日／レンタカー返却日／復路の搭乗日。
 *   つまり「現地に何泊するか」は returnIso − departIso で、宿の泊数と一致する。
 *
 * ⚠️ 上限・下限は src/lib/hotel-params.ts（サーバ側 /api/hotels の正規化）から**import して共有する**。
 *    別々に書くと、画面では選べるのにサーバが黙って別の日付に丸める状態になり、
 *    「表示されている宿一覧」と「外部リンクに載せた日付」が食い違う。
 */
import { addDays, isoDate, shortJp, type Ymd } from './format';
import { MAX_STAY_NIGHTS, MAX_LEAD_DAYS } from './hotel-params';

export interface TripConditions {
  /** 自分の街を出発する日 YYYY-MM-DD（宿チェックイン／レンタカー貸出／往路） */
  departIso: string;
  /** 自分の街に帰り着く日 YYYY-MM-DD（宿チェックアウト／レンタカー返却／復路） */
  returnIso: string;
  /** 大人の人数 */
  adults: number;
  /** 部屋数（宿泊のみ使用） */
  rooms: number;
}

/**
 * ★「予約できる窓」と「旅程を立てられる窓」は別物（2026-08-04の是正）。
 *
 * 以前は出発日の上限を一律 `今日+MAX_LEAD_DAYS(365日)` にしていた。その結果、
 * **1年より先の回**（実例: DESTINY+ & RAMSES = 2028-04-20・DAY精度）を開くと、
 * 打ち上げ日から自動算出した旅程が窓の外なので今日+365日へ丸められ、
 * 画面には「この日程には打ち上げ日（4/20(木)）が含まれていません。日付を見直してください。」
 * という**利用者に非がないエラー**が出ていた。しかも直しようがない
 * （打ち上げ日を入力しようとしても input の max に阻まれる）。
 *
 * 旅程は「打ち上げ日に合っている」のが常に正しい。予約サイトがまだ受け付けていないことは
 * 別途 `isBeyondBookingWindow()` で判定して**伝えるだけ**にし、日付は動かさない。
 */
export const MAX_PLAN_LEAD_DAYS = 1826; // 約5年（LL2に載る最も遠い回でも収まる長さ）

export const MIN_ADULTS = 1;
/** 楽天 VacantHotelSearch の adultNum は 1〜10。UIは現実的な範囲に絞る。 */
export const MAX_ADULTS = 8;
export const MIN_ROOMS = 1;
export const MAX_ROOMS = 4;
/** 最短1泊（当日発・当日帰りは観覧に成立しないので許可しない）。 */
export const MIN_NIGHTS = 1;
export { MAX_STAY_NIGHTS as MAX_NIGHTS, MAX_LEAD_DAYS };

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ---- 日付ヘルパー（すべて "YYYY-MM-DD" の文字列を入出力する） -----------------

/** "2026-09-10" → {y,m,d}。書式が違う／実在しない日付（2026-02-30 等）は null。 */
export function isoToYmd(iso: string | null | undefined): Ymd | null {
  if (!iso || !DATE_RE.test(iso)) return null;
  const [ys, ms, ds] = iso.split('-');
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const dt = new Date(Date.UTC(y, m - 1, d));
  // 「2026-02-30」は Date が3月2日に繰り上げてしまうので、往復して一致するかで実在を検定する
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return { y, m, d };
}

/** 書式・実在ともに正しい日付か。 */
export function isValidIsoDate(iso: string | null | undefined): boolean {
  return isoToYmd(iso) != null;
}

/** JSTの「今日」を YYYY-MM-DD で返す（端末のタイムゾーンに依存させない）。 */
export function todayIsoJst(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** ISO日付に n 日足す。不正な入力はそのまま返す（呼び出し側で検定済みの前提）。 */
export function addDaysIso(iso: string, n: number): string {
  const ymd = isoToYmd(iso);
  return ymd ? isoDate(addDays(ymd, n)) : iso;
}

/** b − a の日数。どちらかが不正なら 0。 */
export function diffDays(aIso: string, bIso: string): number {
  const a = isoToYmd(aIso);
  const b = isoToYmd(bIso);
  if (!a || !b) return 0;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** "9/10(木)" 形式の表示ラベル。 */
export function labelJp(iso: string): string {
  const ymd = isoToYmd(iso);
  return ymd ? shortJp(ymd) : iso;
}

/** 現地の泊数（= 宿の泊数）。 */
export function nightsOf(trip: Pick<TripConditions, 'departIso' | 'returnIso'>): number {
  return Math.max(0, diffDays(trip.departIso, trip.returnIso));
}

// ---- 正規化 -----------------------------------------------------------------

export function clampAdults(n: unknown): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return 2;
  return Math.min(MAX_ADULTS, Math.max(MIN_ADULTS, v));
}

export function clampRooms(n: unknown): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return 1;
  return Math.min(MAX_ROOMS, Math.max(MIN_ROOMS, v));
}

/**
 * 出発日として選べる範囲（`<input type="date">` の min/max にそのまま入れる）。
 *
 * @param launchDayIso 選択中の打ち上げ日。予約窓（今日+365日）より先なら**その日まで窓を広げる**。
 *                     広げるだけで狭めはしない（近い回の挙動は従来どおり）。
 */
export function departRange(todayIso: string, launchDayIso?: string | null): { min: string; max: string } {
  const bookableMax = addDaysIso(todayIso, MAX_LEAD_DAYS);
  if (!launchDayIso || !isValidIsoDate(launchDayIso) || launchDayIso <= bookableMax) {
    return { min: todayIso, max: bookableMax };
  }
  // 打ち上げ日そのものが上限。無限に先へは広げない（壊れたnetで窓が暴走しないように）。
  const planMax = addDaysIso(todayIso, MAX_PLAN_LEAD_DAYS);
  return { min: todayIso, max: launchDayIso < planMax ? launchDayIso : planMax };
}

/**
 * その出発日が「予約サイトの受付窓（今日+365日）」より先か。
 * true のとき /api/hotels は日付を今日へ丸めてしまう（＝黙って別の日を検索する）ので、
 * 呼び出し側は検索そのものを行わず「まだ受付前」と伝えること。
 */
export function isBeyondBookingWindow(departIso: string, todayIso: string): boolean {
  return diffDays(todayIso, departIso) > MAX_LEAD_DAYS;
}

/** 帰着日として選べる範囲（出発日に従属）。 */
export function returnRange(departIso: string): { min: string; max: string } {
  return { min: addDaysIso(departIso, MIN_NIGHTS), max: addDaysIso(departIso, MAX_STAY_NIGHTS) };
}

/**
 * 出発日・帰着日を「今日〜今日+365日」「1〜30泊」の窓に丸める。
 * ★サーバ(/api/hotels)と同じ窓に揃えてあるので、ここを通した値はサーバに黙って書き換えられない。
 *
 * @param changed どちらの入力を利用者が触ったか。触った側を尊重し、もう一方を動かす
 *                （帰着日を出発日より前にしたとき、勝手に出発日を書き換えられると操作不能になる）。
 * @param launchDayIso 選択中の打ち上げ日。1年より先の回でも旅程が打ち上げ日に届くよう窓を広げる。
 */
export function clampDates(
  departRaw: string,
  returnRaw: string,
  todayIso: string,
  changed: 'depart' | 'return' = 'depart',
  launchDayIso?: string | null,
): { departIso: string; returnIso: string } {
  const range = departRange(todayIso, launchDayIso);
  let depart = isValidIsoDate(departRaw) ? departRaw : todayIso;
  if (depart < range.min) depart = range.min;
  if (depart > range.max) depart = range.max;

  let ret = isValidIsoDate(returnRaw) ? returnRaw : addDaysIso(depart, 1);

  if (changed === 'return') {
    // 帰着日を触った → 帰着日を尊重し、必要なら出発日を引き寄せる
    if (ret < range.min) ret = range.min;
    if (ret > addDaysIso(range.max, MAX_STAY_NIGHTS)) ret = addDaysIso(range.max, MAX_STAY_NIGHTS);
    const nights = diffDays(depart, ret);
    if (nights < MIN_NIGHTS) depart = addDaysIso(ret, -MIN_NIGHTS);
    else if (nights > MAX_STAY_NIGHTS) depart = addDaysIso(ret, -MAX_STAY_NIGHTS);
    if (depart < range.min) {
      depart = range.min;
      ret = addDaysIso(depart, Math.min(MAX_STAY_NIGHTS, Math.max(MIN_NIGHTS, diffDays(depart, ret))));
    }
  } else {
    const bounds = returnRange(depart);
    if (ret < bounds.min) ret = bounds.min;
    if (ret > bounds.max) ret = bounds.max;
  }
  return { departIso: depart, returnIso: ret };
}

/** 丸め込み済みの完全な旅程条件を作る。 */
export function normalizeTrip(
  raw: Partial<TripConditions>,
  todayIso: string,
  changed: 'depart' | 'return' = 'depart',
  launchDayIso?: string | null,
): TripConditions {
  const { departIso, returnIso } = clampDates(raw.departIso ?? '', raw.returnIso ?? '', todayIso, changed, launchDayIso);
  return { departIso, returnIso, adults: clampAdults(raw.adults), rooms: clampRooms(raw.rooms) };
}

/**
 * localStorage から復元した日付の採否。
 * 過去日・不正・窓の外なら null を返して「打ち上げから自動算出」に戻す
 * （前回の旅行の日付が残っていて、気づかず古い日付で外部サイトへ飛ぶ事故を防ぐ）。
 *
 * ⚠️ 上限は予約窓(365日)ではなく計画窓(MAX_PLAN_LEAD_DAYS)で見る。
 *    復元は打ち上げデータの読み込み前に走るため、ここで打ち上げ日を知りようがない。
 *    保存値は必ず launchId とセットで、使う直前に clampDates(…, launchDayIso) を通るので、
 *    ここで365日に切ると「1年より先の回で入れた日付」だけが毎回消える。
 */
export function restoreDates(
  departRaw: string | null,
  returnRaw: string | null,
  todayIso: string,
): { departIso: string; returnIso: string } | null {
  if (!isValidIsoDate(departRaw) || !isValidIsoDate(returnRaw)) return null;
  const depart = departRaw as string;
  const ret = returnRaw as string;
  if (depart < todayIso) return null;
  if (depart > addDaysIso(todayIso, MAX_PLAN_LEAD_DAYS)) return null;
  const nights = diffDays(depart, ret);
  if (nights < MIN_NIGHTS || nights > MAX_STAY_NIGHTS) return null;
  return { departIso: depart, returnIso: ret };
}

// ---- 利用者への注意 ---------------------------------------------------------

export interface TripIssue {
  /** error=見に行けない／warn=見に行けるが危うい／info=旅程は正しいが今はまだ予約できない */
  level: 'error' | 'warn' | 'info';
  message: string;
}

/**
 * 「旅程としては正しいが、予約サイトがまだその日を売っていない」ことの案内。
 * ★エラーにしない。利用者の入力に非は無く、直すべきものが何も無いため。
 *
 * 期間の根拠（推測しない）:
 *  - 本サイトの宿検索(/api/hotels)の窓 … 今日+365日（src/lib/hotel-params.ts）
 *  - 楽天レンタカー … 約8か月先まで在庫が登録されない（2026-08-01 実測。src/lib/deeplinks.ts）
 */
export function bookingWindowNotice(departIso: string, todayIso: string): TripIssue | null {
  if (!isBeyondBookingWindow(departIso, todayIso)) return null;
  return {
    level: 'info',
    message:
      `日程は打ち上げ日に合わせてあります。出発が約${Math.floor(diffDays(todayIso, departIso) / 30)}か月先のため、` +
      '宿・航空券・レンタカーはまだ販売が始まっていません（発売はおおむね出発の1年前ごろから）。日付はこのままで大丈夫です。',
  };
}

/**
 * 旅程そのものは成立していても、**観覧の目的に照らしておかしい**組み合わせを指摘する。
 * 「入力が不正」ではなく「その日程だと打ち上げが見られない/延期に耐えられない」を伝えるのが目的。
 *
 * @param launchDayIso 打ち上げ日（JSTのカレンダー日）。仮日程・未定なら null を渡す。
 */
export function tripIssues(trip: TripConditions, launchDayIso: string | null): TripIssue[] {
  const out: TripIssue[] = [];
  const nights = nightsOf(trip);
  if (nights < MIN_NIGHTS) {
    out.push({ level: 'error', message: '帰着日は出発日より後の日を選んでください。' });
    return out;
  }
  if (!launchDayIso) return out;

  if (trip.departIso > launchDayIso || trip.returnIso < launchDayIso) {
    out.push({
      level: 'error',
      message: `この日程には打ち上げ日（${labelJp(launchDayIso)}）が含まれていません。日付を見直してください。`,
    });
    return out;
  }
  if (trip.departIso === launchDayIso) {
    out.push({
      level: 'warn',
      message: '打ち上げ当日に出発する日程です。打ち上げは早朝・午前のことも多いため、前日入りをおすすめします。',
    });
  }
  if (trip.returnIso === launchDayIso) {
    out.push({
      level: 'warn',
      message: '打ち上げ当日に帰る日程です。延期は常態なので、翌日以降に帰る日程のほうが安全です。',
    });
  }
  return out;
}

/** 「9/10(木) 〜 9/13(日)・3泊4日・大人2名1室」 */
export function tripSummaryLabel(trip: TripConditions): string {
  const nights = nightsOf(trip);
  const rooms = trip.rooms > 1 ? `${trip.rooms}室` : '1室';
  return `${labelJp(trip.departIso)} 〜 ${labelJp(trip.returnIso)}・${nights}泊${nights + 1}日・大人${trip.adults}名${rooms}`;
}
